import { useNavigate } from 'react-router-dom';
import { useGenerator } from '../features/generator/context/GeneratorContext';
import LogoForm from '../features/generator/LogoForm';
import RefineForm from '../features/generator/RefineForm';
import EmptyState from '../features/generator/EmptyState';
import LoadingState from '../features/generator/LoadingState';
import LogoResults from '../features/generator/LogoResults';
import LimitReachedModal from '../components/ui/LimitReachedModal';
import SEO from '../components/SEO';

export default function HomePage() {
  const navigate = useNavigate();
  const { state, actions, refs } = useGenerator();

  const {
    prompt,
    transparentBackground,
    colorHints,
    logoType,
    logos,
    activeLogo,
    isLoading,
    isRefining,
    isUploading,
    error,
    uploadedLogo,
    limitReachedType,
  } = state;

  const {
    setPrompt,
    setLogoType,
    setTransparentBackground,
    setColorHints,
    setError,
    handleGenerate,
    handleRefine,
    handleUploadIcon,
    openUploadPicker,
    setActiveLogo,
    setLimitReachedType,
  } = actions;

  const { uploadInputRef } = refs;

  const handleRefineClick = (logoId) => {
    // find logo object
    const logo = logos.find(l => l.id === logoId);
    if (logo) {
      setActiveLogo(logo);
    }
  };

  const handleFinalizeClick = (logoId) => {
    navigate(`/finalize/${logoId}`);
  };

  const hasLogos = logos.length > 0;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // Reset input to allow re-selection

    if (file) {
      await handleUploadIcon(file);
    }
  };

  return (
    <main className="flex-1 app-scroll">
      <SEO canonical="/" />
      <LimitReachedModal
        isOpen={!!limitReachedType}
        type={limitReachedType}
        onClose={() => setLimitReachedType(null)}
      />
      <div className="container-page py-6">
        {error && (
          <div className="mb-4 alert-error">
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto btn-ghost px-2 py-1 text-red-100 hover:bg-white/5 focus:ring-red-400/60"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        <input
          ref={uploadInputRef}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={handleUpload}
        />

        <div className="grid lg:grid-cols-[420px_1fr] gap-6">
          <div className="relative flex flex-col">
            <div className={`transition-all duration-500 ease-in-out ${activeLogo ? 'opacity-0 invisible absolute inset-0 -translate-x-full' : 'opacity-100 visible relative translate-x-0'}`}>
              <LogoForm
                prompt={prompt}
                onPromptChange={setPrompt}
                transparentBackground={transparentBackground}
                onTransparentBackgroundChange={setTransparentBackground}
                colorHints={colorHints}
                onColorHintsChange={setColorHints}
                logoType={logoType}
                onLogoTypeChange={setLogoType}
                onGenerate={handleGenerate}
                onUploadClick={openUploadPicker}
                isLoading={isLoading}
                isUploading={isUploading}
              />
            </div>

            <div className={`transition-all duration-500 ease-in-out ${activeLogo ? 'opacity-100 visible relative translate-x-0' : 'opacity-0 invisible absolute inset-0 translate-x-full'}`}>
              {activeLogo && (
                <RefineForm
                  activeLogo={activeLogo}
                  onBack={() => setActiveLogo(null)}
                  onRefine={handleRefine}
                  transparentBackground={transparentBackground}
                  onTransparentBackgroundChange={setTransparentBackground}
                  isLoading={isLoading || isRefining}
                />
              )}
            </div>
          </div>

          <div className="glass overflow-hidden min-h-[520px]">
            {isLoading || isRefining ? (
              <LoadingState
                message={isRefining ? "AI is refining your icon..." : "AI is creating your icons..."}
              />
            ) : (
              <div className="h-full flex flex-col">
                <div className="flex-1 min-h-0">
                  {hasLogos ? (
                    <LogoResults
                      logos={logos}
                      onFinalize={handleFinalizeClick}
                      onRefine={handleRefineClick}
                      onRegenerate={handleGenerate}
                      onUploadIcon={openUploadPicker}
                    />
                  ) : (
                    <EmptyState onUploadIcon={openUploadPicker} />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
